import { WorksheetImagesAndRemarksSection, WorksheetTestedBySection } from 'Components/Worksheets/Core';
import { hasNonEmptyValue, valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { not } from 'ramda';
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { NamePrefixModifier, withInjectedProps } from '../../../Core/InjectedPropsContext';
import { PageSection } from '../../../Layout';
import { IWorksheetComponent, WorksheetRootComponent } from '../../Core/types';
import ids from '../../QestIds';
import { PortionDebugViewButton } from '../Components';
import { createSeparationCountSelector } from '../Helpers';
import {
  PortionSievingPageSection,
  ResultsPageSection,
  SeparationDuringSievingPageSection,
  SpecimenPreparationPageSection,
  TestPreparationPageSection,
  WashingPageSection,
  WholePortionPreparationSection,
} from './Sections';

import { InitialSeparationPageSection } from './Sections/Separation';
const isDevMode = process.env.REACT_APP_ENABLE_DEV_MODE === 'true';
const qestIds = [ids.psdSieveAnalysis.BS_EN_ISO_17892_4_Clause_5_2.Rev2016];

/**
 * Group of sections required to perform separation and sieve produced portions.
 * Recursive as separations are nested.
 *
 * Name prefix should be the path of the portion being separated
 */
let SeparationSectionGroup: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, namePrefix }): ReactElement => {
  const isInitialSeparation =
    useSelector(
      pipe(createWorksheetSelector!('data.Preparation.InitialSeparationPerformed', false), valueOrDefault(false))
    ) &&
    (namePrefix?.endsWith('WholePortion') ?? false);
  const isSeparationDuringSieving = !isInitialSeparation;

  const isFinePortionSubspecimenWashed = useSelector(
    pipe(createWorksheetSelector!(`Separation.FinePortion.Subspecimen.Washing.IsWashed`), valueOrDefault(false))
  );

  const isLastSeparation = useSelector(
    pipe(
      createWorksheetSelector!('Separation.FinePortion.Subspecimen.Separation'),
      (nextSeparation) => hasNonEmptyValue(nextSeparation),
      not
    )
  );
  return (
    <>
      {isInitialSeparation && <InitialSeparationPageSection />}
      {isInitialSeparation && isFinePortionSubspecimenWashed && (
        <NamePrefixModifier appendNamePrefix={'Separation.FinePortion.Subspecimen'}>
          <WashingPageSection />
        </NamePrefixModifier>
      )}

      <NamePrefixModifier appendNamePrefix={'Separation.CoarsePortion'}>
        {/*<SievingPreparationPageSection />*/}
        <PortionSievingPageSection />
      </NamePrefixModifier>

      {isSeparationDuringSieving && <SeparationDuringSievingPageSection />}

      <NamePrefixModifier appendNamePrefix={'Separation.FinePortion.Subspecimen'}>
        {isSeparationDuringSieving && isFinePortionSubspecimenWashed && <WashingPageSection />}
        {isLastSeparation ? (
          <>
            {/*<SievingPreparationPageSection />*/}
            <PortionSievingPageSection />
          </>
        ) : (
          <SeparationSectionGroup />
        )}
      </NamePrefixModifier>
    </>
  );
};

SeparationSectionGroup = withInjectedProps(SeparationSectionGroup);

const PsdSieveAnalysisBsEnIsoWorksheetBase: WorksheetRootComponent = ({ createWorksheetSelector }) => {
  const isWholePortionSeparated = useSelector(
    pipe(createSeparationCountSelector(createWorksheetSelector!), (c) => c !== 0)
  );

  const isWholePortionWashed = useSelector(
    pipe(createWorksheetSelector!('WholePortion.Washing.IsWashed'), valueOrDefault(false))
  );

  return (
    <>
      {isDevMode && (
        <PageSection>
          <PortionDebugViewButton />
        </PageSection>
      )}

      <TestPreparationPageSection />
      <SpecimenPreparationPageSection />

      <NamePrefixModifier appendNamePrefix={`WholePortion`}>
        <WholePortionPreparationSection />
        {isWholePortionWashed && <WashingPageSection />}
        {isWholePortionSeparated ? <SeparationSectionGroup /> : <PortionSievingPageSection />}
      </NamePrefixModifier>

      <PageSection>
        <WorksheetTestedBySection />
      </PageSection>

      <ResultsPageSection />

      <PageSection heading="Images and Remarks">
        <WorksheetImagesAndRemarksSection />
      </PageSection>
    </>
  );
};

PsdSieveAnalysisBsEnIsoWorksheetBase.qestIds = qestIds;
const PsdSieveAnalysisBsEnIsoWorksheet = withInjectedProps(PsdSieveAnalysisBsEnIsoWorksheetBase);

export default PsdSieveAnalysisBsEnIsoWorksheet;
export { PsdSieveAnalysisBsEnIsoWorksheet };
